import { Formik } from 'formik'
import React, { useRef, useState } from 'react'
import { DocusignFieldsFormValues } from './DocusignFieldsForm.interface'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import { DocusignSigningField } from 'src/interfaces/docusign'
import { getValidationSchema } from './DocusignFieldsForm.validator'
import _ from 'lodash'
import { DocusignFieldsFormInputList } from './DocusignFieldsFormInputList'
import { DocusignFieldTabLabel } from 'src/constants/docusign'
import { Button } from 'src/components/shared/Button'

interface Props {
  fields: Array<DocusignSigningField>
  onSubmit(
    values: Array<{
      tabLabel: string
      value: string
    }>,
  ): Promise<void>
}

export const DocusignFieldsForm = ({ fields, onSubmit }: Props) => {
  const formikRef = useRef<any>()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleFormSubmit = async (values: DocusignFieldsFormValues) => {
    setIsLoading(true)
    setError(null)

    const mmsOregonAddressField = fields.find(
      (field) => field.tabLabel === DocusignFieldTabLabel.mmsOregonAddress,
    )

    try {
      const payload = { ...values }

      if (mmsOregonAddressField && values[DocusignFieldTabLabel.state] === 'OR') {
        payload[DocusignFieldTabLabel.mmsOregonAddress] = `${
          values[DocusignFieldTabLabel.address]
        }, ${values[DocusignFieldTabLabel.city]}, ${values[DocusignFieldTabLabel.state]}, ${
          values[DocusignFieldTabLabel.zipcode]
        }`
      }

      await onSubmit(
        Object.entries(payload).map((item) => ({
          tabLabel: item[0],
          value: !_.isNil(item[1]) ? item[1].toString() : '',
        })),
      )
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div css={tw`w-full max-h-full m-auto overflow-auto`}>
      <div css={tw`max-w-[390px] m-auto flex-col bg-white rounded-2xl overflow-hidden`}>
        <Formik
          initialValues={{
            ...fields.reduce((res, item) => {
              if (item.tabType === 'checkbox') {
                res[item.tabLabel] = item.value || false
              } else {
                res[item.tabLabel] = item.value || ''
              }

              return res
            }, {} as any),
          }}
          innerRef={formikRef}
          validationSchema={getValidationSchema(fields)}
          onSubmit={handleFormSubmit}
        >
          {({ handleSubmit }) => (
            <div css={tw`h-full w-full flex flex-col`}>
              <div css={tw`p-4`}>
                <Typography css={tw`mb-4`} variant="body2" color={colors.SHADES_GREY_400}>
                  To begin signing documents, please provide the following basic information.
                </Typography>
                <DocusignFieldsFormInputList fields={fields} />
                <FormErrorMessage css={tw`mb-4`} error={error} />
              </div>
              <div css={tw`p-4`}>
                <Button
                  size="small"
                  className={tw`w-full`}
                  loading={isLoading}
                  onClick={() => handleSubmit()}
                >
                  Submit and continue
                </Button>
              </div>
            </div>
          )}
        </Formik>
      </div>
    </div>
  )
}
